import React, { useState, useEffect } from 'react';
import { creatorAPI } from '../../services/api';
import { Star, Loader, Save, AlertCircle, CheckCircle, Video } from 'lucide-react';

const OfficialPicksPanel = ({ channelId }) => {
  const [moments, setMoments] = useState([]);
  const [currentPicks, setCurrentPicks] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchMoments = async () => {
      setLoading(true);
      try {
        const data = await creatorAPI.getPeakMoments();
        setMoments(data.peakMoments || []);
        const picks = (data.currentPicks || []).map(id => id.toString());
        setCurrentPicks(picks);
        setSelectedIds(picks);
      } catch (err) {
        console.error('Failed to load peak moments:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchMoments();
  }, [channelId]);

  const togglePick = (id) => {
    const idStr = id.toString();
    setSelectedIds(prev => {
      if (prev.includes(idStr)) return prev.filter(i => i !== idStr);
      if (prev.length >= 5) {
        setMessage('Maximum 5 official picks allowed');
        setTimeout(() => setMessage(''), 3000);
        return prev;
      }
      return [...prev, idStr];
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await creatorAPI.setOfficialPicks(selectedIds);
      setCurrentPicks(selectedIds);
      setMessage('Official picks saved!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to save picks');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(selectedIds.sort()) !== JSON.stringify(currentPicks.sort());

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px', color: '#666', gap: '8px' }}>
        <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> Loading peak moments...
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '20px', padding: '16px 20px', borderRadius: '12px',
        background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.02))',
        border: '1px solid rgba(245,158,11,0.12)',
      }}>
        <div>
          <div style={{ fontSize: '13px', color: '#F59E0B', fontWeight: '600', marginBottom: '2px' }}>
            Curate Your Best Moments
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <span style={{ color: selectedIds.length >= 5 ? '#F59E0B' : '#888', fontWeight: '600' }}>
              {selectedIds.length}/5
            </span> selected &middot; These will be highlighted on your public profile
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '9px 20px', border: 'none', borderRadius: '8px',
            cursor: (!hasChanges || saving) ? 'not-allowed' : 'pointer',
            fontSize: '13px', fontWeight: '600',
            background: (!hasChanges || saving) ? '#222' : 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
            color: '#FFF', opacity: (!hasChanges || saving) ? 0.5 : 1,
            transition: 'all 0.15s',
          }}
        >
          {saving ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving...</>
            : <><Save size={14} /> Save Picks</>}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '10px 16px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
          background: message.includes('fail') || message.includes('Maximum') ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.08)',
          color: message.includes('fail') || message.includes('Maximum') ? '#EF4444' : '#10B981',
          border: `1px solid ${message.includes('fail') || message.includes('Maximum') ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)'}`,
        }}>
          {message.includes('fail') || message.includes('Maximum') ? <AlertCircle size={14} /> : <CheckCircle size={14} />}
          {message}
        </div>
      )}

      {/* Grid */}
      {moments.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 20px', color: '#444', fontSize: '14px',
          background: '#111118', borderRadius: '14px', border: '1px solid #1A1A2E',
        }}>
          No approved peak moments found for your channel yet.
          <br /><span style={{ fontSize: '12px', color: '#333' }}>Peak moments will appear here once community members submit them.</span>
        </div>
      ) : (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px',
        }}>
          {moments.map(m => {
            const isSelected = selectedIds.includes(m._id.toString());
            return (
              <div
                key={m._id}
                onClick={() => togglePick(m._id)}
                style={{
                  background: '#111118', borderRadius: '12px',
                  border: isSelected ? '2px solid #F59E0B' : '1px solid #1A1A2E',
                  overflow: 'hidden', cursor: 'pointer',
                  transition: 'all 0.2s', position: 'relative',
                  boxShadow: isSelected ? '0 0 20px rgba(245,158,11,0.1)' : 'none',
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = '#2A2A3E'; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = '#1A1A2E'; }}
              >
                {/* Star badge */}
                <div style={{
                  position: 'absolute', top: 10, right: 10, zIndex: 2,
                  width: 30, height: 30, borderRadius: '50%',
                  background: isSelected ? '#F59E0B' : 'rgba(0,0,0,0.6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: isSelected ? '2px solid #FCD34D' : '2px solid rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(4px)',
                }}>
                  <Star size={14} fill={isSelected ? '#000' : 'none'} color={isSelected ? '#000' : '#FFF'} />
                </div>
                {isSelected && (
                  <div style={{
                    position: 'absolute', top: 10, left: 10, zIndex: 2,
                    padding: '3px 8px', background: '#F59E0B', color: '#000',
                    fontSize: '10px', fontWeight: 'bold', borderRadius: '4px',
                  }}>Official Pick</div>
                )}

                {/* Thumbnail */}
                {m.videoThumbnail ? (
                  <img src={m.videoThumbnail} alt={m.title} style={{
                    width: '100%', height: '150px', objectFit: 'cover',
                    borderBottom: '1px solid #1A1A2E',
                  }} />
                ) : (
                  <div style={{
                    width: '100%', height: '150px', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    background: '#0A0A0F', borderBottom: '1px solid #1A1A2E',
                  }}>
                    <Video size={28} style={{ color: '#222' }} />
                  </div>
                )}

                {/* Info */}
                <div style={{ padding: '12px 14px' }}>
                  <div style={{
                    fontSize: '13px', fontWeight: '500', color: '#E5E5E5',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    marginBottom: '4px',
                  }}>{m.title}</div>
                  <div style={{ fontSize: '11px', color: '#555' }}>
                    {m.videoTitle} &middot; {m.timestampFormatted} &middot; {m.likes || 0} likes
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OfficialPicksPanel;
